// src/shared/stores/useRequestDetailsStore.ts
import { create } from 'zustand';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/config';
import {getAndSanitize} from '../../shared/utils/sanitization';

interface RequestDetails {
  id: string;
  requestId: string;
  [key: string]: any;
}

interface FetchDetailsOptions {
  includeUserData?: boolean;
  forceRefresh?: boolean; // Add this option to force refresh
}

interface RequestDetailsState {
  details: Record<string, RequestDetails>;
  loading: Record<string, boolean>;
  errors: Record<string, Error | null>;
  lastFetched: Record<string, number>;
  
  fetchDetails: (requestId: string, adminId: string, options?: FetchDetailsOptions) => Promise<RequestDetails | null>;
  fetchCompleteDetails: (requestId: string, adminId: string, options?: FetchDetailsOptions) => Promise<RequestDetails | null>;
  clearDetails: (requestId: string) => void;
  clearAllDetails: () => void;
  invalidateCache: (requestId: string) => void; // New method to invalidate cache
}


// Track ongoing requests to prevent duplicates
const fetchPromises: Record<string, Promise<RequestDetails>> = {};

// Cache time: 30 seconds - reduced from 2 minutes to ensure fresher data
const CACHE_TIME = 30 * 1000;

export const useRequestDetailsStore = create<RequestDetailsState>((set, get) => ({
  details: {},
  loading: {},
  errors: {},
  lastFetched: {},

  fetchCompleteDetails: async (requestId: string, adminId: string, options: FetchDetailsOptions = {}): Promise<RequestDetails | null> => {
    try {
      // 1. Fetch base request details with forceRefresh option passed through
      const detailsData = await get().fetchDetails(requestId, adminId, options);
      if (!detailsData) return null;
      
      // 2. Get additional user data if needed
      let userData = null;
      const username = detailsData.user?.username;
      
      if (options.includeUserData && username) {
        const token = getAndSanitize("token");
        if (!token) throw new Error("Token not found");
        
        try {
          const userResponse = await axios.get(
            `${API_BASE_URL}/api/panel/users`,
            { 
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              params: { adminId }
            }
          );

          userData = userResponse.data.data.find(
            (user: any) => user.username === username
          );
        } catch (error) {
          console.error('[RequestDetailsStore] User data fetch failed:', error);
        }
      }
      
      // 3. Transform data consistently
      const effectiveReceipt = 
        detailsData.receipt || 
        detailsData.operation?.receipt || 
        null;
        
      const historyData = userData?.historyData || [];
      const currentBalance = userData?.currentBalance || 0;
      
      const transformedData: RequestDetails = {
        ...detailsData,
        requestId,
        validationDetails: detailsData.validationDetails ?? null,
        validationScore: detailsData.validationScore ?? null,
        receipt: effectiveReceipt,
        user: {
          ...(detailsData.user || {}),
          ...(userData || {}),
          historyData,
          currentBalance
        },
        requestData: {
          ...detailsData.requestData,
          retryHistory: detailsData.requestData?.retryHistory ?? [],
          processingSteps: detailsData.requestData?.processingSteps?.map((step: any) => ({
            ...step,
            timestamp: new Date(step.timestamp),
            metadata: {
              ...step.metadata,
              detectedPlatform: step.metadata?.detectedPlatform ?? "unknown",
              extractedFields: step.metadata?.extractedFields ?? {},
              comparedTransfers: step.metadata?.comparedTransfers ?? [],
            },
          })) || []
        },
        linkedTransfer: detailsData.linkedTransfer,
        operation: {
          ...detailsData.operation,
          receipt: effectiveReceipt
        }
      };
      
      // Update cache with enhanced data
      set(state => ({
        details: { ...state.details, [requestId]: transformedData }
      }));
      
      return transformedData;
    } catch (error) {
      console.error('[RequestDetailsStore] Complete details fetch failed:', error);
      return null;
    }
  },
  
  fetchDetails: async (requestId: string, adminId: string, options: FetchDetailsOptions = {}) => {
    const { details, lastFetched } = get();
    const currentTime = Date.now();
    
    // Return cached data if fresh AND forceRefresh is not set
    if (
      !options.forceRefresh &&
      details[requestId] && 
      lastFetched[requestId] && 
      currentTime - lastFetched[requestId] < CACHE_TIME
    ) {
      return details[requestId];
    }
    
    // Generate unique request key
    const requestKey = `${requestId}-${adminId}-${options.forceRefresh ? 'force' : 'normal'}`;
    
    // Reuse in-flight request if exists and not forcing refresh
    if (!options.forceRefresh && fetchPromises[requestKey]) {
      return fetchPromises[requestKey];
    }
    
    // Mark as loading
    set(state => ({
      loading: { ...state.loading, [requestId]: true },
      errors: { ...state.errors, [requestId]: null }
    }));
    
    try {
      const token = getAndSanitize("token");
      if (!token) throw new Error("No token found");
      
      fetchPromises[requestKey] = new Promise(async (resolve, reject) => {
        try {
          
          const response = await axios.get(
            `${API_BASE_URL}/api/requests/load-requests/${requestId}/details`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
              },
              params: {
                requestId,
                adminId,
                _nocache: options.forceRefresh ? Date.now() : undefined // Add cache buster to API request
              }
            }
          );
          
          if (!response.data.success) {
            throw new Error(response.data.error || 'Error fetching request details');
          }

          // Process response
          console.log('[RequestDetailsStore] API response raw:', {
            receipt: response.data.data?.receipt,
            operationReceipt: response.data.data?.operation?.receipt,
            hasData: !!response.data.data,
            keys: response.data.data ? Object.keys(response.data.data) : [],
          });
          const detailsData = {
            ...response.data.data,
            requestId // Ensure requestId is always present
          };
          
          // Update store
          set(state => ({
            details: { ...state.details, [requestId]: detailsData },
            loading: { ...state.loading, [requestId]: false },
            lastFetched: { ...state.lastFetched, [requestId]: Date.now() }
          }));
          
          resolve(detailsData);
        } catch (error) {
          const typedError = error as Error;
          set(state => ({
            loading: { ...state.loading, [requestId]: false },
            errors: { ...state.errors, [requestId]: typedError }
          }));
          reject(typedError);
        } finally {
          delete fetchPromises[requestKey];
        }
      });
      
      return await fetchPromises[requestKey];
    } catch (error) {
      console.error('[RequestDetailsStore] Error fetching details:', error);
      return null;
    }
  },
  
  // Invalidate the cache for a specific request
  invalidateCache: (requestId: string) => {
    set(state => ({
      lastFetched: { ...state.lastFetched, [requestId]: 0 } // Set to 0 to force refresh on next fetch
    }));
  },
  
  clearDetails: (requestId: string) => set(state => {
    const { [requestId]: _, ...remainingDetails } = state.details;
    const { [requestId]: __, ...remainingLastFetched } = state.lastFetched;
    const { [requestId]: ___, ...remainingLoading } = state.loading;
    const { [requestId]: ____, ...remainingErrors } = state.errors;
    
    return {
      details: remainingDetails,
      lastFetched: remainingLastFetched,
      loading: remainingLoading,
      errors: remainingErrors
    };
  }),
  
  clearAllDetails: () => set({
    details: {},
    loading: {},
    errors: {},
    lastFetched: {}
  })
}));